import { useQuery } from '@tanstack/react-query';

import type { ApiTimeslot } from '../../common/api/timeslots/timeslotModels';

import timeslotFlow from '../../common/flow/timeslot/timeslotFlow';
import { useAppSelector } from '../../common/hooks/useThunkDispatch';
import { useIsUserAuthenticated } from '../../common/hooks/useUserProfile';
import { cartSelectors } from '../../common/selectors/cartSelectors';
import { storeSelectors } from '../../common/selectors/storeSelectors';
import { useIsB2bCrossContext } from '../../ecommerce/hooks/useIsB2bCrossContext';

export const useTimeslotsForCurrentContext = () => {
    const { privateCartOnCompanySite } = useIsB2bCrossContext();
    const hasActiveStoreSelection = useAppSelector(storeSelectors.hasActiveStoreSelection);
    const isCartInitialized = useAppSelector(cartSelectors.isCartInitialized);
    const { selectedStore: currentStore } = useAppSelector((state) => state.store);
    const selectedZipCode = useAppSelector(cartSelectors.postalCode);
    const isAuthenticated = useIsUserAuthenticated();
    const query = useQuery<ApiTimeslot[], Error>({
        queryKey: ['currentCart', `timeslots:${selectedZipCode}:${currentStore?.id}`],
        queryFn: async () => {
            if (isAuthenticated) {
                return timeslotFlow.fetchForCurrentCart();
            }

            if (selectedZipCode) {
                return timeslotFlow.fetchForPostalCode({
                    b2bRoute: privateCartOnCompanySite,
                    postalCode: selectedZipCode,
                });
            }
            if (currentStore) {
                return timeslotFlow.fetchForPointOfService({
                    b2bRoute: privateCartOnCompanySite,
                    pointOfService: currentStore.id,
                });
            }
            return [];
        },
        enabled: hasActiveStoreSelection && isCartInitialized,
        staleTime: 0, // timeslots can change frequently, so we want to fetch them every time
    });
    return query;
};
